// ─────────────────────────────────────────────────────────────────────
// Access-Management hardening — Round 10: "Change password" (My Profile)
//
// The only self-service way to set a new password while signed in.
//   • current password is verified server-side (BCrypt, or legacy exact
//     match during the transition);
//   • the new password must satisfy the ONE server-side policy
//     (GET /api/Auth/password-policy is displayed, POST enforces it);
//   • on success every OTHER session is revoked (security stamp changed);
//     the server returns a fresh token pair so THIS session continues;
//   • a notification e-mail is sent to the employee (best effort).
// No passwords are logged or stored in React state longer than needed.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Alert, Spinner } from "reactstrap";
import api from "../../api";

export default function ChangePasswordCard() {
    const [policy, setPolicy] = useState({ minLength: 12, maxLength: 128, rules: [] });
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState([]);
    const [done, setDone] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("Auth/password-policy")
            .then((r) => { if (alive && r?.data?.minLength) setPolicy(r.data); })
            .catch(() => { /* keep defaults */ });
        return () => { alive = false; };
    }, []);

    const clientCheck = () => {
        if (!current) return "Enter your current password.";
        if (next.length < policy.minLength) return `The new password must be at least ${policy.minLength} characters.`;
        if (next.length > policy.maxLength) return `The new password must be at most ${policy.maxLength} characters.`;
        if (next !== confirm) return "The new password and its confirmation do not match.";
        if (next === current) return "The new password must be different from the current one.";
        return "";
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setErrors([]); setDone(false);
        const pre = clientCheck();
        if (pre) { setError(pre); return; }
        setBusy(true);
        try {
            const res = await api.post("Auth/change-password", { currentPassword: current, newPassword: next });
            // Keep THIS session alive with the fresh tokens the server returned.
            const data = res?.data || {};
            if (data.token) {
                try {
                    const auth = JSON.parse(sessionStorage.getItem("authUser") || "null") || {};
                    sessionStorage.setItem("authUser", JSON.stringify({ ...auth, ...data }));
                } catch { /* ignore */ }
            }
            setCurrent(""); setNext(""); setConfirm("");
            setDone(true);
        } catch (err) {
            const d = err?.response?.data;
            setError((d && (d.error || d.message)) || (typeof d === "string" ? d : "") || "Could not change the password.");
            if (Array.isArray(d?.errors)) setErrors(d.errors);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mt-3">
            <CardBody>
                <h5 className="mb-1">Change password</h5>
                <p className="text-muted small mb-3">
                    At least {policy.minLength} characters — a short phrase works well. Any characters are allowed.
                    Changing your password signs you out of your other devices.
                </p>
                {error && (
                    <Alert color="danger" className="py-2">
                        {error}
                        {errors.length > 1 && <ul className="mb-0 mt-1">{errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                    </Alert>
                )}
                {done && <Alert color="success" className="py-2">Password changed. A confirmation e-mail has been sent to your address on file.</Alert>}
                <Form onSubmit={submit} autoComplete="off">
                    <FormGroup>
                        <Label htmlFor="cp-current">Current password</Label>
                        <Input id="cp-current" type={show ? "text" : "password"} autoComplete="current-password"
                               value={current} onChange={(e) => setCurrent(e.target.value)} disabled={busy} />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="cp-new">New password</Label>
                        <Input id="cp-new" type={show ? "text" : "password"} autoComplete="new-password"
                               value={next} onChange={(e) => setNext(e.target.value)} disabled={busy}
                               maxLength={policy.maxLength} />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="cp-confirm">Confirm new password</Label>
                        <Input id="cp-confirm" type={show ? "text" : "password"} autoComplete="new-password"
                               value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}
                               maxLength={policy.maxLength} />
                    </FormGroup>
                    <div className="d-flex align-items-center gap-3">
                        <Button color="primary" type="submit" disabled={busy}>
                            {busy ? <><Spinner size="sm" /> Changing…</> : "Change password"}
                        </Button>
                        <label className="small text-muted mb-0">
                            <input type="checkbox" className="me-1" checked={show} onChange={(e) => setShow(e.target.checked)} />
                            Show passwords
                        </label>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
}
